import { useTranslation } from "react-i18next";
import { NotesSection } from "@/shared/ui/components/composed/notes-section/NotesSection";
import { notifications } from '@/shared/ui/components/basic/Notifications';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';
import { useClientNotes } from "../../hooks/useClientNotes";

/**
 * Overview Notes: free-text comments with author, plus add and delete.
 *
 * Thin wrapper around the shared `NotesSection` bound to the clients i18n
 * namespace and `useClientNotes` hook — mirrors the property notes section.
 *
 * @param props - The agency and client identifiers.
 * @returns The rendered notes section.
 */
export function ClientNotesSection({
  agencyId,
  clientId,
}: {
  agencyId: string;
  clientId: string;
}) {
  const { t } = useTranslation("features/clients");
  const { notes, isLoading, addNote, deleteNote } = useClientNotes({
    agencyId,
    clientId,
  });

  const handleSubmit = async (content: string) => {
    const { ok, error } = await addNote(content);
    if (ok) return true;
    // A failure must never be silent (it previously was).
    notifications.show({
      type: "error",
      title: t("profile.overview.notesSaveErrorTitle"),
      message: error || t("profile.overview.notesSaveErrorMessage"),
    });
    return false;
  };

  return (
    <NotesSection
      notes={notes}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onDelete={deleteNote}
      title={t("profile.overview.notesTitle", "Notes about the client")}
      placeholder={t("profile.overview.notesPlaceholder", "Write a note...")}
      addLabel={t("profile.overview.notesAdd", "Add note")}
      emptyLabel={t("profile.overview.notesEmpty", "No notes yet")}
      deleteLabel={t("profile.overview.notesDelete", "Delete note")}
      confirmDeleteMessage={t("profile.overview.notesConfirmDelete", "Delete this note?")}
      loadingLabel={t("profile.overview.notesLoading", "Loading notes...")}
      maxLength={FORM_FIELD_LIMITS.notes}
      testIds={{
        section: "client-notes-section",
        input: "client-note-input",
        submit: "client-note-submit",
        empty: "client-notes-empty",
      }}
      rowTestIdPrefix="client-note"
      deleteTestIdPrefix="delete-note"
    />
  );
}
